import { forwardRef, useId } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseProgressProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export const BaseProgress = forwardRef<HTMLDivElement, BaseProgressProps>(
  function BaseProgress(
    {
      value = 0,
      max = 100,
      label,
      showValue = false,
      valueFormatter,
      appearance,
      className,
      classNames,
      style,
      'aria-label': nativeAriaLabel,
      'aria-labelledby': nativeAriaLabelledBy,
      ...progressProps
    },
    ref,
  ) {
    const generatedLabelId = `${useId()}-label`;

    if (typeof max !== 'number' || !Number.isFinite(max) || max <= 0) {
      throw new TypeError('BaseProgress max must be a positive finite number.');
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new TypeError('BaseProgress value must be a finite number.');
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const safeValue = clamp(value, 0, max);
    const percent = (safeValue / max) * 100;
    const renderedValue =
      valueFormatter?.(safeValue, max) ?? `${Math.round(percent)}%`;
    const hasLabel = hasRenderableNode(label);
    const hasLabelRow = hasLabel || showValue;
    const labelText = typeof label === 'string' ? label : undefined;
    const hasExplicitName =
      nativeAriaLabel !== undefined || nativeAriaLabelledBy !== undefined;
    const generatedAriaLabelledBy =
      hasLabel && labelText === undefined && !hasExplicitName
        ? generatedLabelId
        : undefined;
    const state = safeValue === 0 ? 'empty' : safeValue === max ? 'complete' : 'progress';

    return (
      <div
        {...componentAttributes}
        {...progressProps}
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={safeValue}
        aria-label={
          nativeAriaLabel ??
          (nativeAriaLabelledBy === undefined ? labelText : undefined)
        }
        aria-labelledby={nativeAriaLabelledBy ?? generatedAriaLabelledBy}
        data-monkeys-component="progress"
        data-slot="root"
        data-state={state}
        data-value={safeValue}
        data-max={max}
        className={cn(theme.slots.progressRoot, classNames?.root, className)}
        style={{
          ...theme.styles.progressRoot,
          ...style,
        }}
      >
        {hasLabelRow ? (
          <div
            data-slot="label-row"
            data-state={state}
            className={cn(theme.slots.progressLabelRow, classNames?.labelRow)}
          >
            {hasLabel ? (
              <span
                id={generatedAriaLabelledBy}
                data-slot="label"
                className={cn(theme.slots.progressLabel, classNames?.label)}
              >
                {label}
              </span>
            ) : (
              <span data-slot="label" />
            )}
            {showValue ? (
              <span
                data-slot="value"
                data-value={safeValue}
                className={cn(theme.slots.progressValue, classNames?.value)}
              >
                {renderedValue}
              </span>
            ) : null}
          </div>
        ) : null}
        <div
          data-slot="track"
          data-state={state}
          className={cn(theme.slots.progressTrack, classNames?.track)}
        >
          <div
            data-slot="indicator"
            data-state={state}
            data-value={safeValue}
            className={cn(theme.slots.progressIndicator, classNames?.indicator)}
            style={{
              ...theme.styles.progressIndicator,
              width: `${percent}%`,
            }}
          />
        </div>
      </div>
    );
  },
);
